import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { withFirebase } from "../../../Firebase";
import {
  getLocalStorageData,
  setLocalStorageData,
} from "src/utils/localStorageUtils";
import { getLatestLogin } from "src/utils/getLatestLogin";
import "./index.css";
import ExitStatusBadge from "src/components/shared/ExitStatusBadge";
import { isDropout, isReleased } from "src/utils/exitStatus";

const STORAGE_KEY = "participantLookup__selectedClassrooms";

const FILTERED_EMAILS = ["inaguirre96@gmail.com", "ignacio@stoprecidivism.org"];
const FILTERED_DOMAINS = ["@stoprecidivism.org", "@myreded.com"];

const isFilteredEmail = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  if (FILTERED_EMAILS.includes(lower)) return true;
  return FILTERED_DOMAINS.some((domain) => lower.endsWith(domain));
};

const isWithinPastWeek = (dateStr) => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  return d >= weekAgo;
};

const ParticipantLookup = ({ firebase, cachedUsers, cachedClassrooms }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [allParticipants, setAllParticipants] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [activeFilter, setActiveFilter] = useState("by-classroom");

  // Dropdown state
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedClassIds, setSelectedClassIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [sortKey, setSortKey] = useState("joined");
  const [sortDir, setSortDir] = useState("desc");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const saved = getLocalStorageData(STORAGE_KEY);
    if (saved && Array.isArray(saved)) {
      setSelectedClassIds(saved);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = useCallback(async () => {
    if (!cachedUsers || !cachedClassrooms) return;
    setLoading(true);
    try {
      const classroomsObj = cachedClassrooms;
      const usersObj = cachedUsers;

      const classroomList = Object.keys(classroomsObj)
        .map((key) => ({
          classId: key,
          className: classroomsObj[key].className || key,
          classroomClosed: classroomsObj[key].classroomClosed || false,
        }))
        .filter((c) => !c.classroomClosed)
        .sort((a, b) => a.className.localeCompare(b.className));

      const classNameSet = new Set(classroomList.map((c) => c.className));

      const participants = Object.keys(usersObj)
        .map((key) => ({
          ...usersObj[key],
          uid: key,
        }))
        .filter((u) => !u.isAdmin && !isFilteredEmail(u.email));

      // Tag each participant
      participants.forEach((p) => {
        p.hasClassroom = !!(p.classId && classNameSet.has(p.classId));
        p.isNewThisWeek = isWithinPastWeek(p.createdAt);
      });

      setClassrooms(classroomList);
      setAllParticipants(participants);
    } catch (err) {
      console.error("Error fetching participant data:", err);
    }
    setLoading(false);
  }, [cachedUsers, cachedClassrooms]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter logic
  let filteredParticipants = [];
  if (activeFilter === "all") {
    filteredParticipants = [...allParticipants];
  } else if (activeFilter === "by-classroom") {
    if (selectedClassIds.length === 0) {
      filteredParticipants = allParticipants.filter((p) => p.hasClassroom);
    } else {
      const selectedClassNames = new Set(
        selectedClassIds.map(
          (id) => classrooms.find((c) => c.classId === id)?.className
        )
      );
      filteredParticipants = allParticipants.filter(
        (p) => p.classId && selectedClassNames.has(p.classId)
      );
    }
  } else if (activeFilter === "unassigned") {
    filteredParticipants = allParticipants.filter((p) => !p.hasClassroom);
  } else if (activeFilter === "new-this-week") {
    filteredParticipants = allParticipants.filter((p) => p.isNewThisWeek);
  }

  // Table search
  if (tableSearch) {
    const term = tableSearch.toLowerCase();
    filteredParticipants = filteredParticipants.filter(
      (p) =>
        String(p.displayName || "").toLowerCase().includes(term) ||
        String(p.email || "").toLowerCase().includes(term) ||
        String(p.phoneNumber || p.phone || "").toLowerCase().includes(term)
    );
  }

  // Sort
  const sortMultiplier = sortDir === "asc" ? 1 : -1;
  filteredParticipants.sort((a, b) => {
    if (sortKey === "name") {
      const nameA = (a.displayName || "").toLowerCase();
      const nameB = (b.displayName || "").toLowerCase();
      return nameA.localeCompare(nameB) * sortMultiplier;
    }
    if (sortKey === "lastLogin") {
      const loginA = new Date(getLatestLogin(a) || 0).getTime();
      const loginB = new Date(getLatestLogin(b) || 0).getTime();
      return (loginA - loginB) * sortMultiplier;
    }
    // joined
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return (dateA - dateB) * sortMultiplier;
  });

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return " ↕";
    return sortDir === "asc" ? " ▲" : " ▼";
  };

  const handleToggleClassroom = (classId) => {
    setSelectedClassIds((prev) => {
      const next = prev.includes(classId)
        ? prev.filter((id) => id !== classId)
        : [...prev, classId];
      setLocalStorageData(STORAGE_KEY, next);
      return next;
    });
  };

  const handleSelectAll = () => {
    const filtered = getFilteredDropdownItems();
    const allFilteredIds = filtered.map((r) => r.classId);
    const allSelected = allFilteredIds.every((id) =>
      selectedClassIds.includes(id)
    );
    const next = allSelected
      ? selectedClassIds.filter((id) => !allFilteredIds.includes(id))
      : [...new Set([...selectedClassIds, ...allFilteredIds])];
    setSelectedClassIds(next);
    setLocalStorageData(STORAGE_KEY, next);
  };

  const handleClearSelection = () => {
    setSelectedClassIds([]);
    setLocalStorageData(STORAGE_KEY, []);
  };

  const getFilteredDropdownItems = () => {
    if (!searchTerm) return classrooms;
    const term = searchTerm.toLowerCase();
    return classrooms.filter((c) =>
      c.className.toLowerCase().includes(term)
    );
  };

  const filteredDropdownItems = getFilteredDropdownItems();

  // Stats
  const totalParticipants = allParticipants.filter((p) => p.hasClassroom).length;
  const unassignedCount = allParticipants.filter((p) => !p.hasClassroom).length;
  const newThisWeekCount = allParticipants.filter((p) => p.isNewThisWeek).length;

  if (loading) {
    return (
      <div className="participants__loading">
        <div className="participants__spinner" />
        Loading user data...
      </div>
    );
  }

  return (
    <div className="participants__container">
      <div className="participants__header">
        <h2 className="participants__title">Users Lookup</h2>
        <p className="participants__subtitle">
          Search and filter users across all classrooms
        </p>
      </div>

      {/* Summary Cards */}
      <div className="participants__summary-cards">
        <div
          className={`participants__card ${activeFilter === "all" ? "participants__card--selected" : ""}`}
          onClick={() => setActiveFilter("all")}
        >
          <div className="participants__card-value">{allParticipants.length}</div>
          <div className="participants__card-label">All Users</div>
        </div>
        <div
          className={`participants__card ${activeFilter === "by-classroom" ? "participants__card--selected" : ""}`}
          onClick={() => setActiveFilter("by-classroom")}
        >
          <div className="participants__card-value">{totalParticipants}</div>
          <div className="participants__card-label">In Classrooms</div>
        </div>
        <div
          className={`participants__card ${activeFilter === "unassigned" ? "participants__card--selected" : ""} ${unassignedCount > 0 ? "participants__card--alert" : ""}`}
          onClick={() => setActiveFilter("unassigned")}
        >
          <div className="participants__card-value">{unassignedCount}</div>
          <div className="participants__card-label">No Classroom</div>
        </div>
        <div
          className={`participants__card ${activeFilter === "new-this-week" ? "participants__card--selected" : ""}`}
          onClick={() => setActiveFilter("new-this-week")}
        >
          <div className="participants__card-value">{newThisWeekCount}</div>
          <div className="participants__card-label">New This Week</div>
        </div>
      </div>

      {/* Classroom dropdown (only for by-classroom filter) */}
      {activeFilter === "by-classroom" && (
        <div className="participants__filter-bar">
          <div className="participants__select-wrapper" ref={dropdownRef}>
            <div
              className={`participants__select-toggle ${dropdownOpen ? "participants__select-toggle--open" : ""}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <div className="participants__select-tags">
                {selectedClassIds.length === 0 ? (
                  <span className="participants__select-placeholder">
                    All classrooms ({classrooms.length})
                  </span>
                ) : (
                  <>
                    {selectedClassIds.slice(0, 3).map((id) => {
                      const match = classrooms.find((c) => c.classId === id);
                      return (
                        <span className="participants__tag" key={id}>
                          {match?.className || id}
                          <span
                            className="participants__tag-remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleClassroom(id);
                            }}
                          >
                            &times;
                          </span>
                        </span>
                      );
                    })}
                    {selectedClassIds.length > 3 && (
                      <span className="participants__tag participants__tag--more">
                        +{selectedClassIds.length - 3} more
                      </span>
                    )}
                  </>
                )}
              </div>
              <span
                className={`participants__select-arrow ${dropdownOpen ? "participants__select-arrow--open" : ""}`}
              >
                &#9662;
              </span>
            </div>

            {dropdownOpen && (
              <div className="participants__dropdown">
                <div className="participants__dropdown-search">
                  <input
                    type="text"
                    placeholder="Search classrooms..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="participants__search-input"
                    autoFocus
                  />
                </div>
                <div className="participants__dropdown-actions">
                  <button
                    className="participants__dropdown-action"
                    onClick={handleSelectAll}
                  >
                    {filteredDropdownItems.length > 0 &&
                    filteredDropdownItems.every((r) =>
                      selectedClassIds.includes(r.classId)
                    )
                      ? "Deselect all"
                      : "Select all"}
                  </button>
                  {selectedClassIds.length > 0 && (
                    <button
                      className="participants__dropdown-action participants__dropdown-action--clear"
                      onClick={handleClearSelection}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <ul className="participants__dropdown-list">
                  {filteredDropdownItems.map((c) => (
                    <li
                      key={c.classId}
                      className={`participants__dropdown-item ${selectedClassIds.includes(c.classId) ? "participants__dropdown-item--selected" : ""}`}
                      onClick={() => handleToggleClassroom(c.classId)}
                    >
                      <input
                        type="checkbox"
                        checked={selectedClassIds.includes(c.classId)}
                        readOnly
                        className="participants__checkbox"
                      />
                      <span className="participants__dropdown-item-name">
                        {c.className}
                      </span>
                    </li>
                  ))}
                  {filteredDropdownItems.length === 0 && (
                    <li className="participants__dropdown-empty">
                      No classrooms match "{searchTerm}"
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Table search */}
      <div className="participants__table-search">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={tableSearch}
          onChange={(e) => setTableSearch(e.target.value)}
          className="participants__table-search-input"
        />
        <span className="participants__result-count">
          {filteredParticipants.length} user{filteredParticipants.length !== 1 ? "s" : ""}
        </span>
      </div>

      {/* Table */}
      {filteredParticipants.length === 0 ? (
        <div className="participants__empty">
          <div className="participants__empty-text">
            {activeFilter === "unassigned"
              ? "No users without a classroom."
              : activeFilter === "new-this-week"
                ? "No new users in the past 7 days."
                : "No users found. Adjust your filters above."}
          </div>
        </div>
      ) : (
        <div className="participants__table-container">
          <table className="participants__table">
            <thead>
              <tr>
                <th
                  className="participants__th--sortable"
                  onClick={() => handleSort("name")}
                >
                  Name{sortIndicator("name")}
                </th>
                <th>Email</th>
                <th>Phone</th>
                <th>Classroom</th>
                <th
                  className="participants__th--sortable"
                  onClick={() => handleSort("joined")}
                >
                  Joined{sortIndicator("joined")}
                </th>
                <th
                  className="participants__th--sortable"
                  onClick={() => handleSort("lastLogin")}
                >
                  Last Login{sortIndicator("lastLogin")}
                </th>
                {activeFilter === "new-this-week" && <th>Classroom Status</th>}
              </tr>
            </thead>
            <tbody>
              {filteredParticipants.map((p, idx) => (
                <tr
                  key={p.uid}
                  className={`participants__row ${idx % 2 === 1 ? "participants__row--alt" : ""} ${isDropout(p) ? "participants__row--inactive" : ""} ${isReleased(p) ? "participants__row--released" : ""}`}
                >
                  <td
                    className="participants__name-cell participants__name-cell--clickable"
                    onClick={() => history.push(`/viewUser/${p.uid}`)}
                  >
                    {p.displayName || "Unknown"}
                    <ExitStatusBadge user={p} />
                    {p.isNewThisWeek && activeFilter !== "new-this-week" && (
                      <span className="participants__new-badge">NEW</span>
                    )}
                  </td>
                  <td>{p.email || "\u2014"}</td>
                  <td>{p.phoneNumber || p.phone || "\u2014"}</td>
                  <td>{p.classId || "\u2014"}</td>
                  <td>
                    {p.createdAt
                      ? new Date(p.createdAt).toLocaleDateString()
                      : "\u2014"}
                  </td>
                  <td>
                    {getLatestLogin(p)
                      ? new Date(getLatestLogin(p)).toLocaleDateString()
                      : "\u2014"}
                  </td>
                  {activeFilter === "new-this-week" && (
                    <td>
                      {p.hasClassroom ? (
                        <span className="participants__status-badge participants__status-badge--assigned">
                          Assigned
                        </span>
                      ) : (
                        <span className="participants__status-badge participants__status-badge--unassigned">
                          Unassigned
                        </span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default withFirebase(ParticipantLookup);
